<template>
  <div class="comments-page-context">
    <div class="container g-0 film-comments-page">
      <div class="row g-0 film-comments-page__header">
        <div class="col g-0">
          <h2 class="sub-heading">
            {{ heading }}
            <span class="comment-count-text" data-testid="comments-count">{{ commentsCountText }}</span>
          </h2>
        </div>
      </div>

      <div class="row g-0 leave-comment-container">
        <div class="col g-0">
          <button
            v-if="isAuthenticated"
            class="leave-comment"
            type="button"
            @click="scrollToCommentForm"
          >
            Leave a comment
          </button>
          <a v-else class="leave-comment" :href="loginPath">Log in to leave a comment</a>
        </div>
      </div>

      <div v-if="isAuthenticated" :id="commentFormId" class="row g-0 comment-editor-row">
        <div class="col g-0">
          <div class="comment-editor">
            <label class="comment-editor__label" :for="newCommentInputId">Your comment</label>
            <textarea
              :id="newCommentInputId"
              v-model="newCommentText"
              data-testid="new-comment-input"
              class="comment-editor__input"
              rows="5"
              maxlength="3000"
              placeholder="Write your comment"
            ></textarea>
            <label class="comment-editor__toggle">
              <input
                v-model="newCommentContainsSpoilers"
                data-testid="new-comment-spoiler"
                type="checkbox"
              >
              <span>Contains spoilers</span>
            </label>
            <p v-if="commentSubmitError" class="comment-editor__error">{{ commentSubmitError }}</p>
            <div class="comment-editor__actions">
              <button
                data-testid="new-comment-submit"
                class="follow-discussion-btn comment-editor__submit"
                type="button"
                :disabled="commentsSubmitting || !newCommentText.trim()"
                @click="submitNewComment"
              >
                Post comment
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="commentsError" class="row g-0 comment-empty-row">
        <div class="col g-0">
          <p class="comment-empty-text">{{ commentsError }}</p>
        </div>
      </div>

      <div v-else-if="commentsLoaded && comments.length === 0" class="row g-0 comment-empty-row">
        <div class="col g-0">
          <p class="comment-empty-text">No comments yet.</p>
        </div>
      </div>

      <template v-for="comment in paginatedComments" :key="comment.id">
        <div class="row g-0 comment-row">
          <div class="col g-0">
            <article class="comment" :data-testid="commentTestId()">
              <div class="comment__layout">
                <div class="comment__body-column">
                  <p
                    v-if="isSpoilerHidden(comment)"
                    class="comment-body comment-body--spoiler"
                    :data-testid="commentTestId('spoiler-body')"
                  >
                    This comment contains spoilers.
                  </p>
                  <p
                    v-else
                    class="comment-body"
                    :data-testid="commentTestId('body')"
                  >
                    {{ comment.comment }}
                  </p>
                </div>
                <div class="comment__meta-column comment-meta">
                  <div class="container g-0">
                    <div class="row g-0 comment-meta__identity-row">
                      <div class="col g-0">
                        <a :href="comment.user_href" class="comment-author">{{ comment.username }}</a>
                      </div>
                    </div>
                    <div class="row g-0 comment-meta__identity-row">
                      <div class="col g-0">
                        <p class="ago">{{ commentTimeText(comment) }}</p>
                      </div>
                    </div>
                    <div v-if="comment.contains_spoilers" class="row g-0 mt-4 comment-meta__action-row">
                      <div class="col g-0 reply-links">
                        <button
                          class="reply-link action-link"
                          :data-testid="commentTestId('spoiler-toggle')"
                          type="button"
                          @click="toggleSpoiler(comment.id)"
                        >
                          {{ isSpoilerHidden(comment) ? 'Show spoiler' : 'Hide spoiler' }}
                        </button>
                      </div>
                    </div>
                    <div class="row g-0 mt-4 comment-meta__action-row">
                      <div class="col g-0 reply-links">
                        <button
                          v-if="isAuthenticated"
                          class="reply-link action-link"
                          :data-testid="commentTestId('reply-toggle')"
                          type="button"
                          @click="toggleReplyForm(comment.id)"
                        >
                          + Reply
                        </button>
                        <a v-else class="reply-link" :href="loginPath">+ Reply</a>
                        <span v-if="comment.replies.length > 0">
                          (<span class="show-replies">{{ pluralize(comment.replies.length, 'reply') }}</span>)
                        </span>
                      </div>
                    </div>
                    <div class="row g-0 comment-meta__action-row comment-meta__like-row">
                      <div class="col g-0 reply-links">
                        <span class="comment-meta__like-actions">
                          <span class="comment-meta__like-control">
                            <button
                              v-if="isAuthenticated"
                              class="reply-link action-link"
                              :data-testid="commentTestId('like')"
                              type="button"
                              @click="likeComment(comment.id)"
                            >
                              {{ comment.user_has_liked ? 'Unlike' : '+ Like' }}
                            </button>
                            <a v-else class="reply-link" :href="loginPath">+ Like</a>
                            <span>
                              (<span class="show-replies" :data-testid="commentTestId('like-count')">
                                {{ pluralize(comment.like_count, 'like') }}
                              </span>)
                            </span>
                          </span>
                          <button
                            v-if="isAuthenticated"
                            class="reply-link reply-link--icon action-link"
                            :data-testid="commentTestId('flag')"
                            type="button"
                            :disabled="comment.user_has_flagged"
                            @click="flagComment(comment.id)"
                            :aria-label="comment.user_has_flagged ? 'Flagged' : 'Flag comment'"
                          >
                            <i class="ion-flag" aria-hidden="true"></i>
                          </button>
                          <a v-else class="reply-link reply-link--icon" :href="loginPath" aria-label="Flag comment">
                            <i class="ion-flag" aria-hidden="true"></i>
                          </a>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="comment.like_count > 0" class="likes">
                <p class="liked-by">Liked by:</p>
                <span class="liker">{{ likedByText(comment) }}</span>
              </div>
            </article>
          </div>
        </div>

        <div
          v-if="isAuthenticated && activeReplyTo === comment.id"
          class="row g-0 comment-row reply-row comment-reply-form-row"
        >
          <div class="col g-0 comment-row__inner comment-row__inner--reply">
            <div class="comment comment-reply-form">
              <label class="comment-editor__label" :for="replyInputId(comment.id)">Reply</label>
              <textarea
                :id="replyInputId(comment.id)"
                v-model="replyDrafts[comment.id]"
                data-testid="reply-input"
                class="comment-editor__input"
                rows="4"
                maxlength="3000"
                placeholder="Write your reply"
              ></textarea>
              <label class="comment-editor__toggle">
                <input
                  v-model="replySpoilerFlags[comment.id]"
                  type="checkbox"
                >
                <span>Contains spoilers</span>
              </label>
              <div class="comment-editor__actions">
                <button
                  data-testid="reply-submit"
                  class="follow-discussion-btn comment-editor__submit"
                  type="button"
                  :disabled="commentsSubmitting || !String(replyDrafts[comment.id] || '').trim()"
                  @click="submitReply(comment.id)"
                >
                  Post reply
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-for="reply in comment.replies"
          :key="reply.id"
          class="row g-0 comment-row reply-row"
        >
          <div class="col g-0 comment-row__inner comment-row__inner--reply">
            <article class="comment comment-reply" :data-testid="commentTestId('reply')">
              <div class="comment__layout">
                <div class="comment__body-column">
                  <p
                    v-if="isSpoilerHidden(reply)"
                    class="comment-body comment-body--spoiler"
                  >
                    This comment contains spoilers.
                  </p>
                  <p v-else class="comment-body">{{ reply.comment }}</p>
                </div>
                <div class="comment__meta-column comment-meta">
                  <div class="container g-0">
                    <div class="row g-0 comment-meta__identity-row">
                      <div class="col g-0">
                        <a :href="reply.user_href" class="comment-author">{{ reply.username }}</a>
                      </div>
                    </div>
                    <div class="row g-0 comment-meta__identity-row">
                      <div class="col g-0">
                        <p class="ago">{{ commentTimeText(reply) }}</p>
                      </div>
                    </div>
                    <div v-if="reply.contains_spoilers" class="row g-0 mt-4 comment-meta__action-row">
                      <div class="col g-0 reply-links">
                        <button
                          class="reply-link action-link"
                          type="button"
                          @click="toggleSpoiler(reply.id)"
                        >
                          {{ isSpoilerHidden(reply) ? 'Show spoiler' : 'Hide spoiler' }}
                        </button>
                      </div>
                    </div>
                    <div class="row g-0 comment-meta__action-row comment-meta__like-row">
                      <div class="col g-0 reply-links">
                        <span class="comment-meta__like-actions">
                          <span class="comment-meta__like-control">
                            <button
                              v-if="isAuthenticated"
                              class="reply-link action-link"
                              type="button"
                              @click="likeComment(reply.id)"
                            >
                              {{ reply.user_has_liked ? 'Unlike' : '+ Like' }}
                            </button>
                            <a v-else class="reply-link" :href="loginPath">+ Like</a>
                            <span>
                              (<span class="show-replies">{{ pluralize(reply.like_count, 'like') }}</span>)
                            </span>
                          </span>
                          <button
                            v-if="isAuthenticated"
                            class="reply-link reply-link--icon action-link"
                            type="button"
                            :disabled="reply.user_has_flagged"
                            @click="flagComment(reply.id)"
                            :aria-label="reply.user_has_flagged ? 'Flagged' : 'Flag comment'"
                          >
                            <i class="ion-flag" aria-hidden="true"></i>
                          </button>
                          <a v-else class="reply-link reply-link--icon" :href="loginPath" aria-label="Flag comment">
                            <i class="ion-flag" aria-hidden="true"></i>
                          </a>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="reply.like_count > 0" class="likes">
                <p class="liked-by">Liked by:</p>
                <span class="liker">{{ likedByText(reply) }}</span>
              </div>
            </article>
          </div>
        </div>
      </template>

      <nav
        v-if="commentsTotalPages > 1"
        class="comments-pagination comments-pagination--with-mobile"
        aria-label="Comments pagination"
        data-testid="comments-pagination"
      >
        <ul class="pagination mb-0">
          <li class="page-item" :class="{ disabled: currentCommentsPage <= 1 }">
            <button
              class="page-link"
              data-testid="comments-prev"
              type="button"
              @click="goToCommentsPage(currentCommentsPage - 1)"
            >
              Previous
            </button>
          </li>
          <li
            v-for="pageItem in commentsPaginationItems"
            :key="`comments-${pageItem.key}`"
            class="page-item"
            :class="{ active: pageItem.page === currentCommentsPage, disabled: pageItem.ellipsis }"
          >
            <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
              {{ pageItem.label }}
            </span>
            <button
              v-else
              class="page-link"
              :data-testid="`comments-page-${pageItem.page}`"
              type="button"
              :aria-label="`Go to comments page ${pageItem.page}`"
              @click="goToCommentsPage(pageItem.page)"
            >
              {{ pageItem.label }}
            </button>
          </li>
          <li class="page-item" :class="{ disabled: currentCommentsPage >= commentsTotalPages }">
            <button
              class="page-link"
              data-testid="comments-next"
              type="button"
              @click="goToCommentsPage(currentCommentsPage + 1)"
            >
              Next
            </button>
          </li>
        </ul>
        <CompactMobilePagination
          :current-page="currentCommentsPage"
          :total-pages="commentsTotalPages"
          test-id-prefix="comments"
          @page-change="goToCommentsPage"
        />
      </nav>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import CompactMobilePagination from './CompactMobilePagination.vue'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { compactPaginationItems } from '../utils/pagination'
import '../assets/single-film/comments.css'

const props = defineProps({
  basePath: {
    type: String,
    required: true,
  },
  heading: {
    type: String,
    default: 'Discussion',
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  targetId: {
    type: Number,
    default: null,
  },
  targetType: {
    type: String,
    required: true,
  },
  testIdPrefix: {
    type: String,
    default: 'item',
  },
})

const route = useRoute()
const auth = useAuthStore()
auth.initFromStorage()

const COMMENTS_PER_PAGE = 25

const commentsLoaded = ref(false)
const commentsError = ref('')
const commentSubmitError = ref('')
const commentsSubmitting = ref(false)
const comments = ref([])
const newCommentText = ref('')
const newCommentContainsSpoilers = ref(false)
const activeReplyTo = ref(0)
const replyDrafts = ref({})
const replySpoilerFlags = ref({})
const revealedSpoilers = ref({})
const currentCommentsPage = ref(1)

const isAuthenticated = computed(() => auth.isAuthenticated.value)
const loginPath = computed(() => buildLoginPath(route.fullPath || `${props.basePath}/comments`))
const commentsCountText = computed(() => {
  const count = comments.value.length
  return `(${count} ${count === 1 ? 'comment' : 'comments'})`
})
const commentsTotalPages = computed(() => Math.max(Math.ceil(comments.value.length / COMMENTS_PER_PAGE), 1))
const commentsPaginationItems = computed(() => compactPaginationItems(currentCommentsPage.value, commentsTotalPages.value))
const paginatedComments = computed(() => {
  const start = (currentCommentsPage.value - 1) * COMMENTS_PER_PAGE
  return comments.value.slice(start, start + COMMENTS_PER_PAGE)
})

function buildFilmsomniacPath(username) {
  const normalizedUsername = String(username || '').trim()
  return normalizedUsername ? `/filmsomniacs/${encodeURIComponent(normalizedUsername)}` : '#'
}
const commentFormId = computed(() => `comment-form-${props.targetType}-${props.targetId || 'pending'}`)
const newCommentInputId = computed(() => `new-comment-input-${props.targetType}-${props.targetId || 'pending'}`)

function commentTestId(part = '') {
  return part ? `${props.testIdPrefix}-comment-${part}` : `${props.testIdPrefix}-comment`
}

function replyInputId(commentId) {
  return `reply-input-${props.targetType}-${commentId}`
}

function resetState() {
  commentsLoaded.value = false
  commentsError.value = ''
  commentSubmitError.value = ''
  commentsSubmitting.value = false
  comments.value = []
  newCommentText.value = ''
  newCommentContainsSpoilers.value = false
  activeReplyTo.value = 0
  replyDrafts.value = {}
  replySpoilerFlags.value = {}
  revealedSpoilers.value = {}
  currentCommentsPage.value = 1
}

function normalizeStringList(items) {
  if (!Array.isArray(items)) return []
  return items.map((item) => String(item || '').trim()).filter(Boolean)
}

function normalizeComment(comment) {
  return {
    id: comment.id,
    parent_id: comment.parent_id ?? null,
    level: comment.level ?? 0,
    comment: comment.comment || '',
    contains_spoilers: Boolean(comment.contains_spoilers),
    submit_date: comment.submit_date || null,
    submit_date_text: comment.submit_date_text || '',
    username: comment.username || '',
    user_href: buildFilmsomniacPath(comment.username),
    user_id: comment.user_id ?? null,
    like_count: Number(comment.like_count || 0),
    liked_by_usernames: normalizeStringList(comment.liked_by_usernames || []),
    flag_count: Number(comment.flag_count || 0),
    user_has_liked: Boolean(comment.user_has_liked),
    user_has_flagged: Boolean(comment.user_has_flagged),
    can_reply: Boolean(comment.can_reply),
    replies: Array.isArray(comment.replies) ? comment.replies.map(normalizeComment) : [],
  }
}

function buildAuthHeaders({ includeJson = false } = {}) {
  const headers = {}
  if (includeJson) {
    headers['Content-Type'] = 'application/json'
  }
  if (auth.state.access) {
    headers.Authorization = `Bearer ${auth.state.access}`
  }
  return headers
}

function fetchCommentsRequest(url) {
  if (!auth.state.access) {
    return fetch(url)
  }
  return fetch(url, { headers: buildAuthHeaders() })
}

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`
}

function formatRelativeTime(value, fallback = '') {
  if (!value) return fallback

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  const diffMs = date.getTime() - Date.now()
  const absSeconds = Math.round(Math.abs(diffMs) / 1000)
  const absMinutes = Math.round(absSeconds / 60)
  const absHours = Math.round(absMinutes / 60)
  const absDays = Math.round(absHours / 24)
  const absMonths = Math.round(absDays / 30)
  const rtf = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
    : null

  if (absSeconds < 60) return rtf ? rtf.format(Math.round(diffMs / 1000), 'second') : fallback
  if (absMinutes < 60) return rtf ? rtf.format(Math.round(diffMs / 60000), 'minute') : fallback
  if (absHours < 24) return rtf ? rtf.format(Math.round(diffMs / 3600000), 'hour') : fallback
  if (absDays < 30) return rtf ? rtf.format(Math.round(diffMs / 86400000), 'day') : fallback
  if (absMonths < 12) return rtf ? rtf.format(Math.round(diffMs / 2592000000), 'month') : fallback
  return rtf ? rtf.format(Math.round(diffMs / 31536000000), 'year') : fallback
}

function commentTimeText(comment) {
  return formatRelativeTime(comment.submit_date, comment.submit_date_text)
}

function isSpoilerHidden(comment) {
  return Boolean(comment.contains_spoilers && !revealedSpoilers.value[comment.id])
}

function toggleSpoiler(commentId) {
  revealedSpoilers.value = {
    ...revealedSpoilers.value,
    [commentId]: !revealedSpoilers.value[commentId],
  }
}

function updateCommentThread(commentId, updater, thread = comments.value) {
  return thread.map((comment) => {
    if (comment.id === commentId) {
      return updater(comment)
    }

    if (!comment.replies.length) {
      return comment
    }

    return {
      ...comment,
      replies: updateCommentThread(commentId, updater, comment.replies),
    }
  })
}

function likedByText(comment) {
  const users = normalizeStringList(comment.liked_by_usernames || [])
  if (users.length > 0) {
    return users.join(', ')
  }
  return pluralize(comment.like_count, 'like')
}

async function loadComments() {
  if (!props.isActive || !props.targetId || typeof fetch !== 'function') {
    return
  }

  commentsLoaded.value = false
  commentsError.value = ''

  try {
    const response = await fetchCommentsRequest(`/api/v1/comments/${props.targetType}/${props.targetId}/`)
    if (!response.ok) {
      commentsError.value = 'Comments could not be loaded.'
      comments.value = []
      return
    }

    const data = await response.json()
    comments.value = Array.isArray(data.results) ? data.results.map(normalizeComment) : []
    currentCommentsPage.value = 1
  } catch {
    commentsError.value = 'Comments could not be loaded.'
    comments.value = []
  } finally {
    commentsLoaded.value = true
  }
}

async function postComment(payload) {
  const response = await fetch(`/api/v1/comments/${props.targetType}/${props.targetId}/`, {
    method: 'POST',
    headers: buildAuthHeaders({ includeJson: true }),
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    throw new Error('Comment could not be created')
  }

  return normalizeComment(await response.json())
}

async function submitNewComment() {
  const comment = newCommentText.value.trim()
  if (!comment || commentsSubmitting.value || !props.targetId) return

  commentsSubmitting.value = true
  commentSubmitError.value = ''

  try {
    const created = await postComment({
      comment,
      contains_spoilers: newCommentContainsSpoilers.value,
    })
    comments.value = [created, ...comments.value]
    currentCommentsPage.value = 1
    newCommentText.value = ''
    newCommentContainsSpoilers.value = false
  } catch {
    commentSubmitError.value = 'Comment could not be posted.'
  } finally {
    commentsSubmitting.value = false
  }
}

async function toggleReplyForm(commentId) {
  if (!isAuthenticated.value) return
  const isOpening = activeReplyTo.value !== commentId
  activeReplyTo.value = isOpening ? commentId : 0

  if (isOpening) {
    await nextTick()
    document.getElementById(replyInputId(commentId))?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

async function submitReply(commentId) {
  const draft = String(replyDrafts.value[commentId] || '').trim()
  if (!draft || commentsSubmitting.value || !props.targetId) return

  commentsSubmitting.value = true
  commentSubmitError.value = ''

  try {
    const created = await postComment({
      comment: draft,
      reply_to: commentId,
      contains_spoilers: Boolean(replySpoilerFlags.value[commentId]),
    })
    comments.value = updateCommentThread(commentId, (comment) => ({
      ...comment,
      replies: [...comment.replies, created],
    }))
    replyDrafts.value = {
      ...replyDrafts.value,
      [commentId]: '',
    }
    replySpoilerFlags.value = {
      ...replySpoilerFlags.value,
      [commentId]: false,
    }
    activeReplyTo.value = 0
  } catch {
    commentSubmitError.value = 'Reply could not be posted.'
  } finally {
    commentsSubmitting.value = false
  }
}

async function likeComment(commentId) {
  if (!isAuthenticated.value) return

  const response = await fetch(`/api/v1/comments/${commentId}/like/`, {
    method: 'POST',
    headers: buildAuthHeaders(),
  })
  if (!response.ok) return

  const data = await response.json()
  comments.value = updateCommentThread(commentId, (comment) => {
    const currentUser = auth.state.username || ''
    const likedUsers = normalizeStringList(comment.liked_by_usernames || [])
    const nextUsers = data.liked
      ? (currentUser && !likedUsers.includes(currentUser) ? [...likedUsers, currentUser] : likedUsers)
      : likedUsers.filter((username) => username !== currentUser)

    return {
      ...comment,
      like_count: Number(data.like_count || 0),
      user_has_liked: Boolean(data.liked),
      liked_by_usernames: nextUsers,
    }
  })
}

async function flagComment(commentId) {
  if (!isAuthenticated.value) return

  const response = await fetch(`/api/v1/comments/${commentId}/flag/`, {
    method: 'POST',
    headers: buildAuthHeaders(),
  })
  if (!response.ok) return

  const data = await response.json()
  comments.value = updateCommentThread(commentId, (comment) => ({
    ...comment,
    flag_count: Number(data.flag_count || 0),
    user_has_flagged: Boolean(data.flagged),
  }))
}

function scrollToCommentForm() {
  const target = document.getElementById(commentFormId.value)
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function goToCommentsPage(page) {
  const nextPage = Math.min(Math.max(Number(page) || 1, 1), commentsTotalPages.value)
  currentCommentsPage.value = nextPage
}

watch(commentsTotalPages, (totalPages) => {
  if (currentCommentsPage.value > totalPages) {
    currentCommentsPage.value = totalPages
  }
})

watch(
  [() => props.isActive, () => props.targetType, () => props.targetId],
  async ([isActive]) => {
    resetState()
    if (isActive) {
      await loadComments()
    }
  },
  { immediate: true },
)
</script>
